import React from "react";
import { ArrowRight } from "lucide-react";
import { useDashTheme } from "../../../hooks/useDashTheme";
import { ToolIcon } from "../../../components/classic/panels";
import { CATS, useCatColors, fmtDur, fmtClockTz } from "../bits";
import { Sk } from "./storyBits";

const UNTAGGED = "Untagged";

function uniqKeep(list, limit = 8) {
  const out = [];
  const seen = new Set();
  for (const raw of list || []) {
    const s = String(raw || "").trim();
    if (!s) continue;
    const k = s.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(s);
    if (out.length >= limit) break;
  }
  return out;
}

function clockRange(startMs, endMs, offsetMin) {
  if (!startMs || !endMs) return "";
  return `${fmtClockTz(startMs, offsetMin)} – ${fmtClockTz(endMs, offsetMin)}`;
}

function sittingWindows(th, offsetMin) {
  const spans = (th.spans || []).slice().sort((a, b) => a.startMs - b.startMs);
  if (!spans.length && th.firstMs && th.lastMs) {
    return clockRange(th.firstMs, th.lastMs, offsetMin);
  }
  if (spans.length <= 3) {
    return spans.map((s) => clockRange(s.startMs, s.endMs, offsetMin)).filter(Boolean).join(" · ");
  }
  return `${clockRange(spans[0].startMs, spans[0].endMs, offsetMin)} · +${spans.length - 1} more sittings`;
}

function buildModel(threads, blocks, totals) {
  const list = (threads || []).filter((th) => th && th.minutes > 0);
  if (!list.length) return null;

  const dayMin = Math.max(1, totals?.trackedMin || list.reduce((a, th) => a + th.minutes, 0));
  const byKey = {};
  for (const b of blocks || []) {
    if (!b.threadKey) continue;
    (byKey[b.threadKey] = byKey[b.threadKey] || []).push(b);
  }

  const enriched = list.map((th) => {
    const mine = byKey[th.key] || [];
    const artifacts = uniqKeep([
      ...(th.artifacts || []).map((a) => (typeof a === "string" ? a : a?.name)),
      ...mine.flatMap((b) => b.artifacts || []),
      ...(th.sessionsList || []).map((s) => s.title).filter((x) => x && x !== th.apps?.[0]),
    ], 7);
    const firstMs = th.firstMs || (th.spans || []).reduce((m, s) => Math.min(m, s.startMs), Infinity);
    const lastMs = th.lastMs || (th.spans || []).reduce((m, s) => Math.max(m, s.endMs), 0);
    return {
      ...th,
      artifacts,
      firstMs: Number.isFinite(firstMs) ? firstMs : null,
      lastMs: lastMs || null,
      project: th.project || null,
    };
  });

  const groups = new Map();
  for (const th of enriched) {
    const name = th.project || UNTAGGED;
    if (!groups.has(name)) groups.set(name, { name, tagged: !!th.project, threads: [], minutes: 0, cats: {}, topics: [] });
    const g = groups.get(name);
    g.threads.push(th);
    g.minutes += th.minutes;
    g.cats[th.cat] = (g.cats[th.cat] || 0) + th.minutes;
    g.topics.push(...(th.topics || []));
  }

  const rolled = [...groups.values()]
    .map((g) => ({
      ...g,
      threads: g.threads.sort((a, b) => b.minutes - a.minutes),
      mix: Object.entries(g.cats).sort((a, b) => b[1] - a[1]),
      topics: uniqKeep(g.topics, 6),
      share: Math.round((g.minutes / dayMin) * 100),
    }))
    .sort((a, b) => {
      if (a.tagged !== b.tagged) return a.tagged ? -1 : 1;
      return b.minutes - a.minutes;
    });

  const catMin = {};
  const topicHit = {};
  for (const th of enriched) {
    catMin[th.cat] = (catMin[th.cat] || 0) + th.minutes;
    for (const tp of th.topics || []) topicHit[tp] = (topicHit[tp] || 0) + 1;
  }
  const mix = Object.entries(catMin).sort((a, b) => b[1] - a[1]);
  const topTopics = Object.entries(topicHit).sort((a, b) => b[1] - a[1]).map(([n]) => n).slice(0, 6);
  const named = rolled.filter((g) => g.tagged);
  const lead = mix[0];

  const bits = [];
  if (named.length === 1) bits.push(`The day sat on ${named[0].name} (${fmtDur(named[0].minutes)}).`);
  else if (named.length > 1) bits.push(`Worked across ${named.length} projects: ${named.map((g) => g.name).join(", ")}.`);
  else bits.push("No project was tagged on this day — below is the work itself.");
  if (lead) bits.push(`Mostly ${CATS[lead[0]]?.label || lead[0]} (${fmtDur(lead[1])}).`);
  if (topTopics.length) bits.push(`About ${topTopics.slice(0, 4).join(", ")}.`);

  return {
    dayMin,
    groups: rolled,
    mix,
    topTopics,
    named,
    juice: bits.join(" "),
    threadCount: enriched.length,
    projectCount: named.length,
  };
}

export function DayJuiceSk() {
  return (
    <div aria-hidden="true">
      <Sk w="78%" h={14} style={{ marginBottom: 14 }} />
      <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
        <Sk w={88} h={22} r={99} /><Sk w={72} h={22} r={99} /><Sk w={96} h={22} r={99} />
      </div>
      {[0, 1].map((i) => (
        <div key={i} style={{ marginTop: i ? 16 : 0 }}>
          <Sk w="36%" h={13} style={{ marginBottom: 10 }} />
          <div className="st-day-tiles">
            <Sk h={88} r={12} /><Sk h={88} r={12} />
          </div>
        </div>
      ))}
    </div>
  );
}

export function DayJuice({ threads, blocks, totals, bounds, offsetMin, onOpen }) {
  const t = useDashTheme();
  const C = useCatColors();
  const model = React.useMemo(() => buildModel(threads, blocks, totals), [threads, blocks, totals]);

  if (!model) {
    return (
      <div style={{ fontSize: 13, color: t.c.textTertiary, padding: "4px 0 8px" }}>
        No work threads on this day to report.
      </div>
    );
  }

  const chip = (label, key) => (
    <span key={key} style={{
      fontSize: 11.5, fontWeight: t.font.weight.medium, color: t.c.textSecondary,
      background: t.isDark ? t.c.surfaceMuted : t.c.surfaceHover,
      borderRadius: t.r.pill, padding: "4px 10px", whiteSpace: "nowrap",
    }}>{label}</span>
  );

  return (
    <div>
      <div style={{
        display: "flex", alignItems: "flex-start", justifyContent: "space-between",
        gap: 16, flexWrap: "wrap", marginBottom: 12,
      }}>
        <p style={{
          margin: 0, flex: "1 1 280px", fontSize: 13.5, lineHeight: 1.5, color: t.c.textSecondary,
        }}>{model.juice}</p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, justifyContent: "flex-end", flex: "1 1 240px" }}>
          {model.named.map((g) => chip(`${g.name} · ${fmtDur(g.minutes)}`, g.name))}
          {model.mix.map(([cat, min]) => chip(`${CATS[cat]?.label || cat} · ${fmtDur(min)}`, cat))}
          {model.topTopics.slice(0, 4).map((tp) => chip(tp, `t-${tp}`))}
        </div>
      </div>

      {model.groups.map((g) => (
          <div key={g.name} style={{ marginBottom: 12 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 6 }}>
              <div style={{ fontSize: 14, fontWeight: 650, color: t.c.text, letterSpacing: "-0.01em" }}>
                {g.name}
              </div>
              <div style={{ fontSize: 12, color: t.c.textTertiary, fontVariantNumeric: "tabular-nums" }}>
                {fmtDur(g.minutes)} · {g.share}% of the day
                {g.mix.length > 1 ? ` · ${g.mix.map(([c, m]) => `${CATS[c]?.label || c} ${fmtDur(m)}`).join(" · ")}` : ""}
              </div>
            </div>

            <div className="st-day-tiles">
              {g.threads.map((th) => {
                const col = C[th.cat] || C.browse;
                const blurb = th.outcomes?.[0]?.text;
                const when = sittingWindows(th, offsetMin);
                return (
                  <button
                    key={th.key}
                    type="button"
                    className="st-reset st-hit st-focusable"
                    onClick={() => onOpen?.(th)}
                    style={{
                      display: "block", width: "100%", textAlign: "left", cursor: "pointer",
                      padding: "12px 13px 11px",
                      background: t.isDark ? t.c.surfaceMuted : "#F7F8FB",
                      border: `1px solid ${t.c.border}`,
                      borderRadius: t.r.md,
                    }}
                  >
                    <span style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                      <ToolIcon name={(th.apps || [])[0] || ""} cat={th.cat} size={32} />
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: "flex", alignItems: "baseline", gap: 8, minWidth: 0 }}>
                          <span style={{
                            fontSize: 13.5, fontWeight: 600, color: t.c.text, letterSpacing: "-0.01em",
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                          }} title={th.title}>{th.title}</span>
                          <span style={{
                            flex: "none", fontSize: 12, fontWeight: 650, color: col, fontVariantNumeric: "tabular-nums",
                          }}>{fmtDur(th.minutes)}</span>
                        </span>
                        <span style={{ display: "block", marginTop: 3, fontSize: 12, color: t.c.textTertiary }}>
                          {CATS[th.cat]?.label || th.cat}
                          {th.stretches > 1 ? ` · ${th.stretches} sittings` : ""}
                          {when ? ` · ${when}` : ""}
                        </span>
                        {blurb && (
                          <span style={{
                            display: "block", marginTop: 6, fontSize: 13, lineHeight: 1.45, color: t.c.textSecondary,
                          }}>{blurb}</span>
                        )}
                        {(th.topics?.length > 0 || th.artifacts.length > 0) && (
                          <span style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                            {(th.topics || []).slice(0, 4).map((tp) => (
                              <span key={tp} style={{
                                fontSize: 11, fontWeight: 600, color: col,
                                background: t.isDark ? `${col}22` : `${col}14`,
                                borderRadius: t.r.pill, padding: "3px 8px",
                              }}>{tp}</span>
                            ))}
                            {th.artifacts.slice(0, 5).map((a) => (
                              <span key={a} style={{
                                fontSize: 11, fontWeight: 500, color: t.c.textSecondary,
                                background: t.isDark ? t.c.surface : "#fff",
                                border: `1px solid ${t.c.border}`,
                                borderRadius: t.r.pill, padding: "3px 8px",
                              }}>{a}</span>
                            ))}
                          </span>
                        )}
                        {(th.apps || []).length > 0 && (
                          <span style={{ display: "block", marginTop: 7, fontSize: 11.5, color: t.c.textTertiary }}>
                            In {(th.apps || []).slice(0, 4).join(", ")}
                          </span>
                        )}
                      </span>
                      <span className="st-go" style={{
                        marginTop: 2, width: 26, height: 26, borderRadius: "50%", flex: "none",
                        border: `1.5px solid ${t.c.border}`, color: t.c.textTertiary,
                        display: "grid", placeItems: "center",
                      }}><ArrowRight size={12} /></span>
                    </span>
                    {bounds?.startMs && (th.spans || []).length > 0 && (
                      <span aria-hidden="true" style={{
                        display: "block", position: "relative", height: 5, borderRadius: 99,
                        overflow: "hidden", marginTop: 10,
                        background: t.isDark ? t.c.surface : t.c.surfaceHover,
                      }}>
                        {(th.spans || []).map((s, j) => {
                          const span = Math.max(1, (bounds.endMs || 0) - bounds.startMs);
                          return (
                            <span key={j} style={{
                              position: "absolute", top: 0, bottom: 0, borderRadius: 99, background: col,
                              left: `${Math.max(0, ((s.startMs - bounds.startMs) / span) * 100)}%`,
                              width: `${Math.max(1.4, ((s.endMs - s.startMs) / span) * 100)}%`,
                            }} />
                          );
                        })}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
      ))}

      <div style={{ fontSize: 12, color: t.c.textTertiary }}>
        {model.threadCount} thread{model.threadCount === 1 ? "" : "s"}
        {model.projectCount ? ` · ${model.projectCount} project${model.projectCount === 1 ? "" : "s"}` : ""}
        {" · "}open a row for the full read
      </div>
    </div>
  );
}
